import React, { useState, useRef, useCallback } from 'react';
import './cards.css';

const DualCard = ({ card, scale = 1, interactive = true, showBack = false }) => {
  const [flipped, setFlipped] = useState(false);
  const cardRef = useRef(null);
  const rainbowRef = useRef(null);
  const reflectionRef = useRef(null);

  const lastMove = useRef(0);
  const handleMouseMove = useCallback((e) => {
    const now = Date.now();
    if (now - lastMove.current < 16) return;
    lastMove.current = now;
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    el.style.transform = `perspective(800px) rotateY(${(x - 0.5) * 12}deg)`;
    el.style.setProperty('--lx', `${50 + (0.5 - x) * 12}%`);
    if (rainbowRef.current) { rainbowRef.current.style.opacity = '1'; rainbowRef.current.style.backgroundPosition = `${x * 100}% ${y * 100}%`; }
    if (reflectionRef.current) { reflectionRef.current.style.opacity = '1'; reflectionRef.current.style.transform = `translateX(${(x - 0.5) * 200}%)`; }
  }, []);

  const handleMouseLeave = useCallback(() => {
    const el = cardRef.current;
    if (el) el.style.transform = 'perspective(800px) rotateY(0deg)';
    if (el) { el.style.setProperty('--lx', '50%'); el.style.setProperty('--ly', '20%'); }
    if (rainbowRef.current) rainbowRef.current.style.opacity = '0.5';
    if (reflectionRef.current) reflectionRef.current.style.opacity = '0.25';
  }, []);

  const {
    legendary = false,
    player1, player2,
    description = '',
    numbering,
    leagueLogo = 'https://upload.wikimedia.org/wikipedia/commons/a/a6/Major_League_Baseball_logo.svg',
    auto1Path, auto2Path,
    auto1Image, auto2Image,
    auto1Color = 'rgba(200,40,40,1)',
    auto2Color = 'rgba(40,80,220,1)',
  } = card;

  const getPhoto = (p) => p.photoUrl || (p.sport === 'nhl'
    ? `https://assets.nhle.com/mugs/actionshots/1296x729/${p.playerId}.jpg`
    : `./cards/photos/players/${p.playerId}.jpg`);

  const getHeadshot = (p) => p.sport === 'nhl'
    ? `https://assets.nhle.com/mugs/nhl/latest/${p.playerId}.png`
    : `https://img.mlbstatic.com/mlb-photos/image/upload/w_200,q_auto:best/v1/people/${p.playerId}/headshot/silo/current`;

  const getTeamLogo = (p) => p.sport === 'nhl'
    ? `https://assets.nhle.com/logos/nhl/svg/${p.team}_light.svg`
    : `https://www.mlbstatic.com/team-logos/${p.teamLogoId}.svg`;

  const panelBg = legendary
    ? 'linear-gradient(to bottom, #0a0a14, #151525, #0a0a14)'
    : 'linear-gradient(to bottom, #ffffff, #f0f0f0, #e8e8e8)';

  const cardBg = '#ffffff';
  const diamondEdges = legendary
    ? 'linear-gradient(135deg, rgba(140,180,220,0.5) 0%, rgba(140,180,220,0.2) 15%, transparent 28%), linear-gradient(225deg, rgba(140,180,220,0.5) 0%, rgba(140,180,220,0.2) 15%, transparent 28%), linear-gradient(315deg, rgba(160,200,240,0.4) 0%, rgba(160,200,240,0.15) 12%, transparent 25%), linear-gradient(45deg, rgba(160,200,240,0.4) 0%, rgba(160,200,240,0.15) 12%, transparent 25%)'
    : 'none';

  return (
    <div className="bdc-card-outer bdc-dual-outer" ref={interactive ? cardRef : undefined} onMouseMove={interactive ? handleMouseMove : undefined} onMouseLeave={interactive ? handleMouseLeave : undefined} style={{ transition: interactive ? 'transform 0.25s ease-out' : 'none', ...(scale !== 1 ? { width: `${450 * scale}px`, height: `${320 * scale}px`, overflow: 'hidden' } : {}) }}>
      <div className="bdc-dual-scaler" style={scale !== 1 ? { transform: `scale(${scale})`, transformOrigin: 'top left' } : undefined}>
      <div className={`bdc-dual-flipper ${flipped ? 'flipped' : ''}`} onClick={interactive ? () => setFlipped(!flipped) : undefined}>

        {/* FRONT */}
        <div className="bdc-card-face bdc-dual-front">
          <div style={{ position: 'absolute', inset: 0, background: cardBg, zIndex: 0 }} />
          {legendary && <div style={{ position: 'absolute', inset: 0, background: diamondEdges, zIndex: 1 }} />}
          {interactive && legendary && <>
            <div className="bdc-rare999-refractor-lines" />
            <div className="bdc-chrome-specular bdc-chrome-mask" />
            <div ref={rainbowRef} className="bdc-chrome-rainbow bdc-chrome-mask" />
            <div ref={reflectionRef} className="bdc-chrome-reflection bdc-chrome-mask" />
            <div className="bdc-chrome-edge" />
          </>}
          {!legendary && interactive && <div className="bdc-shimmer" />}

          {/* Two photos */}
          <div className={`bdc-dual-photo left ${legendary ? 'bdc-dual-fade' : 'bdc-dual-mask'}`}>
            <img src={getPhoto(player1)} alt={player1.name} style={{ objectPosition: player1.photoPosition || 'center 15%', ...(player1.photoStyle || {}) }} />
          </div>
          <div className={`bdc-dual-photo right ${legendary ? 'bdc-dual-fade' : 'bdc-dual-mask'}`}>
            <img src={getPhoto(player2)} alt={player2.name} style={{ objectPosition: player2.photoPosition || '60% 10%', ...(player2.photoStyle || {}) }} />
          </div>

          {/* Divider or center area */}
          {legendary ? (
            <div className="bdc-dual-center">
              {numbering && <div className="bdc-dual-numbering">{numbering}</div>}
              <div className="bdc-dual-patches">
                <div className="bdc-dual-patch"><img src={leagueLogo} alt="League" /></div>
                <div className="bdc-dual-patch"><img src={leagueLogo} alt="League" /></div>
              </div>
              <div className="bdc-dual-patch-sub">Game-Used Bases</div>
            </div>
          ) : (
            <div className="bdc-dual-divider" />
          )}

          {/* Autographs (legendary only) */}
          {legendary && (auto1Image || auto1Path) && (
            auto1Image ? (
              <img className="bdc-dual-auto-left bdc-auto-img-dual" src={auto1Image} alt="Auto 1" />
            ) : (
              <svg className="bdc-dual-auto-left" viewBox="0 0 200 60" xmlns="http://www.w3.org/2000/svg">
                <path d={auto1Path} fill="none" stroke={auto1Color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )
          )}
          {legendary && (auto2Image || auto2Path) && (
            auto2Image ? (
              <img className="bdc-dual-auto-right bdc-auto-img-dual" src={auto2Image} alt="Auto 2" />
            ) : (
              <svg className="bdc-dual-auto-right" viewBox="0 0 200 60" xmlns="http://www.w3.org/2000/svg">
                <path d={auto2Path} fill="none" stroke={auto2Color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )
          )}

          {/* Bottom bar */}
          <div className="bdc-dual-bottom" style={{ background: panelBg }}>
            <div className="bdc-dual-names">
              <div className="bdc-dual-names-outline" />
              <div className="bdc-dual-names-fill" />
              <span className="bdc-dual-name">{player1.name}</span>
              <span className="bdc-dual-slash">/</span>
              <span className="bdc-dual-name">{player2.name}</span>
            </div>
            <div className="bdc-dual-desc" style={{ ...(card.descLeft ? { textAlign: 'left' } : {}), ...(legendary ? { color: '#ffffff' } : {}) }}>{description}</div>
          </div>

          <img className="bdc-dual-league-logo" src={leagueLogo} alt="League" onError={(e) => e.target.style.display='none'} />
          <div className={`bdc-bd-logo ${card.bdTop ? 'bdc-bd-logo-top' : ''}`}><div className="bdc-bd-bar" /><div className="bdc-bd-tick" />BD</div>
        </div>

        {/* BACK */}
        {(interactive || showBack) && (
        <div className="bdc-card-face bdc-dual-back">
          <div className="bdc-dual-back-title">{legendary ? `Legendary ${numbering || '1/1'}` : 'Rare /50'}</div>
          <div className="bdc-dual-back-players">
            {[player1, player2].map((p, idx) => (
              <React.Fragment key={idx}>
                {idx === 1 && <div className="bdc-dual-back-divider" />}
                <div className="bdc-dual-back-col">
                  <div className="bdc-dual-back-header">
                    <img className="bdc-dual-back-headshot" src={getHeadshot(p)} alt={p.name} onError={(e) => e.target.style.display='none'} />
                    <div className="bdc-dual-back-info">
                      <div className="bdc-dual-back-pname">{p.name}</div>
                      <div className="bdc-dual-back-pmeta">{p.team} · {p.position} · #{p.number}</div>
                    </div>
                  </div>
                  <table className="bdc-stats-table" style={{ fontSize: '8px' }}>
                    <thead><tr>{(p.statHeaders || []).map((h, i) => <th key={i} style={{ fontSize: '6px' }}>{h}</th>)}</tr></thead>
                    <tbody>
                      {(p.stats || []).map((row, i) => (
                        <tr key={i} className={i === (p.stats || []).length - 1 ? 'highlight' : ''}>
                          {row.map((cell, j) => <td key={j} style={{ fontSize: '8px' }}>{cell}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </React.Fragment>
            ))}
          </div>
          <div className="bdc-back-footer">
            <div className="bdc-card-number">{card.cardNumber || ''}</div>
            <div className="bdc-rarity-tag" style={{ background: legendary ? 'linear-gradient(135deg, #0a0a14, #151525)' : '#2563eb' }}>
              {legendary ? `Dual Patch ${numbering || '1/1'}` : 'Rare /50'}
            </div>
          </div>
        </div>
        )}

      </div>
      </div>
    </div>
  );
};

export default React.memo(DualCard);
